import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useFavorites, getFavoriteItems } from "@/hooks/useLocalLibrary";
import { typeLabels } from "@/mocks/filters";
import { formatDate } from "@/hooks/useMediaQuery";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/category/variety", label: "综艺" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites, toggle, count } = useFavorites();
  const favItems = getFavoriteItems(favorites);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setFavOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const word = keyword.trim();
    navigate(word ? `/search?q=${encodeURIComponent(word)}` : "/search");
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
          scrolled
            ? "border-background-200/80 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[68px] md:gap-8 md:px-6">
          <Link to="/" className="group flex shrink-0 items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-play-fill text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-[17px] font-bold tracking-tight text-foreground-950">
                97视频
              </span>
              <span className="hidden font-label text-[10px] uppercase tracking-[0.18em] text-foreground-500 sm:block">
                97 Video
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-50 font-medium text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="ml-auto hidden md:block">
            <div className="flex h-9 w-56 items-center gap-2 rounded-md border border-background-300/80 bg-background-100/70 px-3 transition-colors focus-within:border-primary-300 lg:w-64">
              <i className="ri-search-line flex h-4 w-4 items-center justify-center text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视标题、主演"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-sm px-1 text-xs text-foreground-500 transition-colors hover:text-accent-600"
              >
                搜索
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => setFavOpen(true)}
            className="relative ml-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/80 text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600 md:ml-0"
            aria-label="我的收藏"
          >
            <i className="ri-heart-3-line text-base" />
            {count > 0 ? (
              <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] font-semibold text-background-50">
                {count}
              </span>
            ) : null}
          </button>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/80 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            aria-label="打开菜单"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-lg`} />
          </button>
        </div>

        {mobileOpen ? (
          <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 lg:hidden">
            <form onSubmit={submitSearch} className="mb-4">
              <div className="flex h-11 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3">
                <i className="ri-search-line flex h-4 w-4 items-center justify-center text-foreground-500" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视标题、主演"
                  className="w-full bg-transparent text-sm outline-none"
                />
                <button type="submit" className="whitespace-nowrap text-sm text-accent-600">
                  搜索
                </button>
              </div>
            </form>
            <ul className="flex flex-col">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                        isActive ? "bg-primary-50 font-medium text-primary-700" : "text-foreground-800"
                      }`
                    }
                  >
                    <span>{item.label}</span>
                    <i className="ri-arrow-right-s-line flex h-4 w-4 items-center justify-center text-foreground-400" />
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </header>

      {favOpen ? (
        <div className="fixed inset-0 z-[80] flex justify-end">
          <button
            type="button"
            aria-label="关闭收藏面板"
            onClick={() => setFavOpen(false)}
            className="animate-fade-in absolute inset-0 cursor-default bg-foreground-950/40"
          />
          <aside className="scroll-thin animate-fade-up relative h-full w-full max-w-sm overflow-y-auto bg-background-50 px-5 py-6">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <p className="font-label text-[11px] uppercase tracking-[0.2em] text-accent-600">My List</p>
                <h2 className="mt-1 font-heading text-lg font-semibold text-foreground-950">我的收藏</h2>
              </div>
              <button
                type="button"
                onClick={() => setFavOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            {favItems.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 px-4 py-12 text-center">
                <i className="ri-heart-3-line mx-auto flex h-10 w-10 items-center justify-center text-2xl text-foreground-400" />
                <p className="mt-3 text-sm text-foreground-700">还没有收藏任何影视</p>
                <p className="mt-1 text-xs text-foreground-500">在详情页点击收藏，稍后可以在这里快速找到</p>
              </div>
            ) : (
              <ul className="flex flex-col gap-3">
                {favItems.map((item) => (
                  <li key={item.id} className="flex gap-3 rounded-lg border border-background-200 bg-background-100/60 p-2">
                    <Link to={`/detail/${item.id}`} className="block h-24 w-16 shrink-0 overflow-hidden rounded-md">
                      <img src={item.cover} alt={item.title} className="h-full w-full object-cover object-top" />
                    </Link>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/detail/${item.id}`}
                        className="line-clamp-1-custom text-sm font-medium text-foreground-950 hover:text-accent-600"
                      >
                        {item.title}
                      </Link>
                      <p className="mt-1 font-label text-[11px] text-foreground-500">
                        {typeLabels[item.type]} · {item.year} · {formatDate(item.updatedAt)}
                      </p>
                      <div className="mt-2 flex items-center gap-3">
                        <Link
                          to={`/detail/${item.id}`}
                          className="text-[11px] text-primary-600 hover:text-accent-600"
                        >
                          查看详情
                        </Link>
                        <button
                          type="button"
                          onClick={() => toggle(item.id)}
                          className="cursor-pointer text-[11px] text-foreground-500 hover:text-accent-600"
                        >
                          取消收藏
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      ) : null}
    </>
  );
}